document.addEventListener('DOMContentLoaded', function () {
    'use strict';

    // =============================================================================
    // CONFIGURATION & CONSTANTS
    // =============================================================================
    const CONFIG = {
        slider: { visible: 9, step: 1 },
        animation: {
            duration: 600,
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            exitEasing: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)',
            delay: 50
        },
        hover: {
            openDelay: 50
        },
        selectors: {
            buttons: '.main-mega-nav-btn',
            panels: '.main-mega-menu-panel',
            slider: '.custom-mega-menu-slider',
            grid: '.custom-mega-menu-slider .custom-mega-menu-cards',
            submenuSlider: '.custom-mega-submenu-slider',
            menuContainer: '.st_mega_menu_container'
        }
    };

    // =============================================================================
    // DOM ELEMENTS CACHE
    // =============================================================================
    const elements = {
        buttons: document.querySelectorAll(CONFIG.selectors.buttons),
        panels: document.querySelectorAll(CONFIG.selectors.panels),
        menuContainer: document.querySelector(CONFIG.selectors.menuContainer)
    };

    // =============================================================================
    // UTILITY FUNCTIONS
    // =============================================================================

    /**
     * Add animation class with delay
     */
    function addAnimationClass(element, className, delay = 0) {
        if (!element) return;
        setTimeout(() => element.classList.add(className), delay);
    }

    /**
     * Debounce function for performance optimization
     */
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Safe querySelector with error handling
     */
    function safeQuerySelector(parent, selector) {
        try {
            return parent ? parent.querySelector(selector) : null;
        } catch (error) {
            console.warn(`Query selector error for ${selector}:`, error);
            return null;
        }
    }

    /**
     * Convert text to sentence case (first letter uppercase, rest lowercase)
     * Only applies if text is all uppercase
     */
    function toSentenceCase(text) {
        if (!text || typeof text !== 'string') return text;
        // Check if text is all uppercase (ignoring spaces and special chars)
        const hasUppercase = /[A-Z]/.test(text);
        const hasLowercase = /[a-z]/.test(text);
        // Only transform if text is all uppercase or mostly uppercase
        if (hasUppercase && !hasLowercase) {
            return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
        }
        return text;
    }

    /**
     * Apply sentence case to category names in the mega menu
     */
    function formatCategoryNames() {
        // Format main menu titles
        document.querySelectorAll('.custom-mega-menu-title').forEach(element => {
            const originalText = element.textContent.trim();
            const formattedText = toSentenceCase(originalText);
            if (formattedText !== originalText) {
                element.textContent = formattedText;
            }
        });

        // Format submenu links
        document.querySelectorAll('.custom-mega-submenu-link').forEach(element => {
            const originalText = element.textContent.trim();
            const formattedText = toSentenceCase(originalText);
            if (formattedText !== originalText) {
                element.textContent = formattedText;
            }
        });

        // Format submenu slider cards (dynamically created)
        document.querySelectorAll('.custom-mega-submenu-card .custom-mega-menu-title').forEach(element => {
            const originalText = element.textContent.trim();
            const formattedText = toSentenceCase(originalText);
            if (formattedText !== originalText) {
                element.textContent = formattedText;
            }
        });
    }

    // =============================================================================
    // ANIMATION FUNCTIONS
    // =============================================================================

    /**
     * Smooth animation to show element
     */
    function animateIn(element, animationType = 'slideDownFromTop', delay = 0) {
        if (!element) return;

        const isSlideIn = animationType === 'slideInFromLeft';
        element.style.opacity = '0';
        element.style.transform = isSlideIn ? 'translateX(-50px)' : 'translateY(-30px)';
        element.style.display = 'block';

        setTimeout(() => {
            element.style.transition = `all ${CONFIG.animation.duration}ms ${CONFIG.animation.easing}`;
            element.style.opacity = '1';
            element.style.transform = 'translateX(0) translateY(0)';
            addAnimationClass(element, `animate-${animationType}`, delay);
        }, 25);
    }

    /**
     * Smooth animation to hide element
     */
    function animateOut(element, callback, animationType = 'slideOutToRight') {
        if (!element) {
            callback?.();
            return;
        }

        const isSlideOut = animationType === 'slideOutToRight';
        element.style.transition = `all 400ms ${CONFIG.animation.exitEasing}`;
        element.style.opacity = '0';
        element.style.transform = isSlideOut ? 'translateX(50px)' : 'translateY(-30px)';

        setTimeout(() => {
            element.style.display = 'none';
            callback?.();
        }, 400);
    }

    // =============================================================================
    // SLIDER MANAGEMENT
    // =============================================================================

    /**
     * Handle slider arrows with hover and auto-hide functionality
     */
    function toggleSliderArrows(leftArrow, rightArrow, show) {
        [leftArrow, rightArrow].forEach(arrow => {
            if (!arrow) return;

            if (show) {
                arrow.style.display = 'flex';
                arrow.classList.add('visible');

                // Auto-hide after 3 seconds
                setTimeout(() => {
                    arrow?.classList.remove('visible');
                }, 3000);
            } else {
                arrow.classList.remove('visible');
                setTimeout(() => arrow.style.display = 'none', 300);
            }
        });
    }

    /**
     * Update vertical arrows visibility based on scroll state
     * @param {HTMLElement} slider - container che contiene frecce e cards
     * @param {HTMLElement} [scrollEl] - elemento che scrolla (se diverso da slider)
     */
    function updateVerticalArrows(slider, scrollEl) {
        if (!slider) return;
        const el = scrollEl || slider;

        const topArrow = slider.querySelector('.vertical-scroll-arrow.top');
        const bottomArrow = slider.querySelector('.vertical-scroll-arrow.bottom');
        if (!topArrow || !bottomArrow) return;

        const isDesktop = window.innerWidth > 768;

        if (isDesktop) {
            const cardsContainer = slider.querySelector('.custom-mega-submenu-cards');
            const firstCard = cardsContainer?.querySelector('.custom-mega-submenu-card');

            if (firstCard) {
                const cardHeight = firstCard.offsetHeight +
                    (parseInt(window.getComputedStyle(cardsContainer).gap) || 0);
                const containerHeight = el.clientHeight;
                const totalHeight = el.scrollHeight;
                const totalRows = Math.ceil(totalHeight / cardHeight);
                const hasAtLeast3Rows = totalRows >= 3;
                const canScrollUp = el.scrollTop > 0;
                const canScrollDown = el.scrollTop < (el.scrollHeight - el.clientHeight - 1);

                if (hasAtLeast3Rows && (canScrollUp || canScrollDown)) {
                    topArrow.classList.toggle('visible', canScrollUp);
                    topArrow.classList.toggle('disabled', !canScrollUp);
                    bottomArrow.classList.toggle('visible', canScrollDown);
                    bottomArrow.classList.toggle('disabled', !canScrollDown);
                } else {
                    topArrow.classList.remove('visible');
                    bottomArrow.classList.remove('visible');
                }
            } else {
                topArrow.classList.remove('visible');
                bottomArrow.classList.remove('visible');
            }
        } else {
            const canScrollUp = el.scrollTop > 0;
            const canScrollDown = el.scrollTop < (el.scrollHeight - el.clientHeight - 1);

            topArrow.classList.toggle('visible', canScrollUp);
            topArrow.classList.toggle('disabled', !canScrollUp);
            bottomArrow.classList.toggle('visible', canScrollDown);
            bottomArrow.classList.toggle('disabled', !canScrollDown);
        }
    }

    /**
     * Create and manage submenu grid with scroll functionality
     */
    function createSubmenuGrid(slider, subcats, parentName) {
        const cardsContainer = slider.querySelector('.custom-mega-submenu-cards');
        if (!cardsContainer) return;
        const title = slider.querySelector('.custom-mega-submenu-slider-title');
        if (title) {
            title.textContent = parentName ? toSentenceCase(parentName) : (subcats[0] ? toSentenceCase(subcats[0].name) : '');
        }

        // Etichetta CTA tradotta (da data-cta-label nel template)
        const ctaLabel = slider.getAttribute('data-cta-label') || 'Scopri la gamma';

        // Render all subcategories in a grid layout
        cardsContainer.innerHTML = subcats.map((sub, index) => {
            const formattedName = toSentenceCase(sub.name);
            return `
            <div class="custom-mega-submenu-card" style="animation-delay: ${index * CONFIG.animation.delay}ms">
                <a href="${sub.link}">
                    <img src="${sub.img}" alt="${formattedName}" class="custom-mega-menu-img" />
                    <span class="custom-mega-submenu-card-text">
                        <span class="custom-mega-menu-title">${formattedName}</span>
                        <span class="custom-mega-submenu-card-cta">${ctaLabel}</span>
                    </span>
                </a>
            </div>
        `;
        }).join('');

        // Frecce presenti nel template: solo binding click e aggiornamento stato visible/disabled
        const topArrow = slider.querySelector('.vertical-scroll-arrow.top');
        const bottomArrow = slider.querySelector('.vertical-scroll-arrow.bottom');
        const scrollEl = slider.querySelector('.custom-mega-submenu-slider-scroll') || slider;

        if (topArrow && bottomArrow) {
            const scrollAmount = () => scrollEl.clientHeight * 0.8;

            topArrow.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                scrollEl.scrollBy({ top: -scrollAmount(), behavior: 'smooth' });
            };

            bottomArrow.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                scrollEl.scrollBy({ top: scrollAmount(), behavior: 'smooth' });
            };

            if (!scrollEl._scrollHandler) {
                let scrollTimeout;
                scrollEl._scrollHandler = () => {
                    clearTimeout(scrollTimeout);
                    scrollTimeout = setTimeout(() => updateVerticalArrows(slider, scrollEl), 50);
                };
                scrollEl.addEventListener('scroll', scrollEl._scrollHandler, { passive: true });
            }

            if (!slider._resizeHandler) {
                slider._resizeHandler = debounce(() => {
                    updateVerticalArrows(slider, scrollEl);
                }, 150);
                window.addEventListener('resize', slider._resizeHandler);
            }

            setTimeout(() => updateVerticalArrows(slider, scrollEl), 100);
            setTimeout(() => updateVerticalArrows(slider, scrollEl), 500);
        }

        // Show grid with animation
        animateIn(slider, 'slideInFromLeft', 100);
        slider.style.display = 'block';
    }

    // =============================================================================
    // PANEL MANAGEMENT
    // =============================================================================

    /**
     * Helper: Reset a panel (submenu, grid, etc.)
     */
    function resetPanel(panel) {
        try {
            // Hide all submenu grids and reset opacity of main items
            panel.querySelectorAll(CONFIG.selectors.submenuSlider).forEach(slider => {
                slider.style.display = 'none';
                slider.style.opacity = '0';
            });

            // Reset opacity of all main menu items
            const mainGrid = safeQuerySelector(panel, CONFIG.selectors.grid);

            if (mainGrid) {
                const itemsToReset = mainGrid.querySelectorAll('.custom-mega-menu-item');

                itemsToReset.forEach((item, i) => {
                    item.classList.remove('submenu-inactive', 'submenu-active');
                    item.style.display = ''; // Ensure all items are visible after reset
                });
            }

            // Reset main slider if needed
            const slider = safeQuerySelector(panel, CONFIG.selectors.slider);
            if (slider) {
                const cardsContainer = slider.querySelector('.custom-mega-menu-cards');
                const leftArrow = slider.querySelector('.slider-arrow.left');
                const rightArrow = slider.querySelector('.slider-arrow.right');

                if (cardsContainer?.classList.contains('is-slider')) {
                    cardsContainer.classList.remove('is-slider');
                    slider.classList.remove('slider-padding');

                    cardsContainer.querySelectorAll('.custom-mega-menu-item').forEach(el => {
                        el.classList.remove('slider-visible', 'slider-inactive', 'slider-active');
                        el.style.display = '';
                    });

                    toggleSliderArrows(leftArrow, rightArrow, false);
                }
            }
        } catch (error) {
            console.error('❌ Errore in resetPanel:', error);
        }
    }

    /**
     * Accordion + slideDown animation for panel
     */
    function animateInPanel(panel) {
        try {
            // Reset to initial state
            Object.assign(panel.style, {
                display: 'block',
                opacity: '0',
                transform: 'translateY(-30px)',
                maxHeight: '0'
            });
            panel.classList.add('active');

            // Force reflow
            panel.offsetHeight;

            const onTransitionEnd = () => {
                ['opacity', 'transform', 'maxHeight'].forEach(prop => panel.style[prop] = '');
                panel.removeEventListener('transitionend', onTransitionEnd);
            };

            panel.addEventListener('transitionend', onTransitionEnd);

            // Trigger CSS transition
            Object.assign(panel.style, {
                opacity: '1',
                transform: 'translateY(0)',
                maxHeight: '1000px'
            });

        } catch (error) {
            console.error('❌ Errore in animateInPanel:', error);
        }
    }

    /**
     * Accordion + slideUp animation for panel (closing)
     */
    function animateOutPanel(panel, callback) {
        try {
            const onTransitionEnd = () => {
                panel.classList.remove('active');
                panel.style.display = 'none';
                ['opacity', 'transform', 'maxHeight'].forEach(prop => panel.style[prop] = '');
                panel.removeEventListener('transitionend', onTransitionEnd);
                callback?.();
            };

            panel.addEventListener('transitionend', onTransitionEnd);

            // Trigger CSS transition
            Object.assign(panel.style, {
                opacity: '0',
                transform: 'translateY(-30px)',
                maxHeight: '0'
            });

        } catch (error) {
            console.error('❌ Errore in animateOutPanel:', error);
        }
    }

    // =============================================================================
    // EVENT HANDLERS
    // =============================================================================

    /** Timeout for delayed open on hover enter */
    let hoverOpenTimeout = null;

    /**
     * Open a menu panel (used for hover)
     */
    function handleMenuOpen(button) {
        try {
            const targetMenuId = button.getAttribute('data-menu');
            const targetPanel = document.getElementById(targetMenuId);

            if (!targetMenuId || !targetPanel) return;

            const currentPanel = document.querySelector('.main-mega-menu-panel.active');
            const currentButton = document.querySelector('.main-mega-nav-btn.active');

            if (targetPanel.classList.contains('active')) return;

            currentButton?.classList.remove('active');
            if (currentPanel && currentPanel !== targetPanel) {
                animateOutPanel(currentPanel, () => resetPanel(currentPanel));
            }

            button.classList.add('active');
            animateInPanel(targetPanel);
        } catch (error) {
            console.error('❌ Errore in handleMenuOpen:', error);
        }
    }

    /**
     * Close the currently open menu panel (used for hover leave / click outside)
     */
    function handleMenuClose() {
        const currentPanel = document.querySelector('.main-mega-menu-panel.active');
        const currentButton = document.querySelector('.main-mega-nav-btn.active');
        if (currentPanel) {
            animateOutPanel(currentPanel, () => resetPanel(currentPanel));
        }
        currentButton?.classList.remove('active');
    }

    /**
     * Handle submenu item clicks
     */
    function handleSubmenuClick(item) {
        let subcats = [];
        try {
            subcats = JSON.parse(item.getAttribute('data-subcats') || '[]');
        } catch (err) {
            console.warn('Invalid subcats data:', err);
            return;
        }

        const panel = item.closest('.main-mega-menu-panel');
        const slider = safeQuerySelector(panel, CONFIG.selectors.submenuSlider);
        if (!slider) return;

        // Hide main grid with smooth animation and reduce opacity of other items
        const grid = safeQuerySelector(panel, CONFIG.selectors.grid);

        if (grid) {
            const allItems = grid.querySelectorAll('.custom-mega-menu-item');
            const clickedIndex = Array.from(allItems).indexOf(item);

            // Reduce opacity of all items except the clicked one
            allItems.forEach((el, i) => {
                if (el === item) {
                    el.classList.remove('submenu-inactive');
                    el.classList.add('submenu-active');
                } else {
                    el.classList.add('submenu-inactive');
                }
            });
        }

        // Nome della categoria parent (voce di menu cliccata)
        const parentName = item.querySelector('.custom-mega-menu-title')?.textContent?.trim() || '';
        createSubmenuGrid(slider, subcats, parentName);

        // Ensure submenu slider is always visible when subcategories exist
        if (subcats && subcats.length > 0) {
            slider.style.display = 'block';
            slider.style.opacity = '1';
        }
    }

    // =============================================================================
    // MAIN SLIDER INITIALIZATION
    // =============================================================================

    /**
     * Initialize a single slider with animations
     */
    function initSingleSlider(slider) {
        if (!slider) return;

        const cardsContainer = slider.querySelector('.custom-mega-menu-cards');
        if (!cardsContainer) return;

        const items = Array.from(cardsContainer.querySelectorAll('.custom-mega-menu-item'));
        const leftArrow = slider.querySelector('.slider-arrow.left');
        const rightArrow = slider.querySelector('.slider-arrow.right');
        let start = 0;

        // Hide arrows by default
        toggleSliderArrows(leftArrow, rightArrow, false);

        // Arrow hover events
        const handleArrowHover = (show) => {
            if (cardsContainer.classList.contains('is-slider')) {
                [leftArrow, rightArrow].forEach(arrow => {
                    arrow?.classList[show ? 'add' : 'remove']('visible');
                });
            }
        };

        slider.addEventListener('mouseenter', () => handleArrowHover(true));
        slider.addEventListener('mouseleave', () => handleArrowHover(false));

        function setSliderMode(isSlider, focusIndex) {
            if (isSlider) {
                cardsContainer.classList.add('is-slider');
                slider.classList.add('slider-padding');
                toggleSliderArrows(leftArrow, rightArrow, true);

                start = typeof focusIndex === 'number'
                    ? Math.floor(focusIndex / CONFIG.slider.visible) * CONFIG.slider.visible
                    : 0;
                updateSlider();
            } else {
                cardsContainer.classList.remove('is-slider');
                slider.classList.remove('slider-padding');

                items.forEach(el => {
                    el.classList.remove('slider-visible', 'slider-inactive', 'slider-active');
                    el.style.display = '';
                });

                toggleSliderArrows(leftArrow, rightArrow, false);
            }
        }

        function updateSlider() {
            items.forEach((el, i) => {
                const isVisible = (i >= start && i < start + CONFIG.slider.visible);
                el.classList.toggle('slider-visible', isVisible);

                if (isVisible) {
                    el.style.display = '';
                    el.classList.add('slider-active');
                    el.classList.remove('slider-inactive');
                    addAnimationClass(el, 'animate-scale-in', (i - start) * CONFIG.animation.delay);
                } else {
                    el.classList.remove('slider-active');
                    el.classList.add('slider-inactive');
                    el.style.display = 'none';
                }
            });

            // Update arrow states
            [leftArrow, rightArrow].forEach((arrow, index) => {
                if (!arrow) return;

                arrow.style.display = 'flex';
                if (index === 0) { // Left arrow
                    arrow.disabled = start === 0;
                } else { // Right arrow
                    arrow.disabled = start + CONFIG.slider.visible >= items.length;
                }

                // Show arrows briefly when updating slider
                arrow.classList.add('visible');
                setTimeout(() => {
                    if (!slider.matches(':hover')) {
                        arrow.classList.remove('visible');
                    }
                }, 2000);
            });
        }

        // Arrow click handlers
        if (leftArrow) {
            leftArrow.onclick = () => {
                if (start > 0) {
                    start -= CONFIG.slider.step;
                    updateSlider();
                }
            };
        }

        if (rightArrow) {
            rightArrow.onclick = () => {
                if (start + CONFIG.slider.visible < items.length) {
                    start += CONFIG.slider.step;
                    updateSlider();
                }
            };
        }

        // Click on category with subcategories
        items.forEach((item, idx) => {
            if (item.classList.contains('has-submenu')) {
                item.addEventListener('click', (e) => {
                    e.preventDefault();
                    // Always set slider mode to show the correct page
                    setSliderMode(true, idx);
                });
            }
        });
    }

    /**
     * Initialize all main sliders with animations
     */
    function initMainSlider() {
        const sliders = document.querySelectorAll(CONFIG.selectors.slider);
        if (!sliders || sliders.length === 0) return;

        // Initialize each slider
        sliders.forEach(slider => {
            initSingleSlider(slider);
        });
    }

    // =============================================================================
    // EVENT LISTENERS SETUP
    // =============================================================================

    // Main menu buttons: open on hover (first level only)
    elements.buttons.forEach((btn, index) => {
        if (btn.classList.contains('is-direct-link')) return;

        btn.addEventListener('mouseenter', () => {
            if (hoverOpenTimeout) clearTimeout(hoverOpenTimeout);
            hoverOpenTimeout = setTimeout(() => {
                hoverOpenTimeout = null;
                handleMenuOpen(btn);
            }, CONFIG.hover.openDelay);
        });

        btn.addEventListener('mouseleave', () => {
            if (hoverOpenTimeout) {
                clearTimeout(hoverOpenTimeout);
                hoverOpenTimeout = null;
            }
            // Non chiudere più in uscita hover: si chiude solo a click esterno o ESC
        });

        // Click: open if closed, close if already open (toggle)
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            if (hoverOpenTimeout) clearTimeout(hoverOpenTimeout);
            hoverOpenTimeout = null;

            const targetMenuId = btn.getAttribute('data-menu');
            const targetPanel = targetMenuId ? document.getElementById(targetMenuId) : null;
            const isAlreadyOpen = targetPanel && targetPanel.classList.contains('active');

            if (isAlreadyOpen) {
                animateOutPanel(targetPanel, () => resetPanel(targetPanel));
                btn.classList.remove('active');
            } else {
                handleMenuOpen(btn);
            }
        });

        // Initial animation for buttons
        setTimeout(() => {
            addAnimationClass(btn, 'animate-slide-in-top', index * 100);
        }, 200);
    });

    // Il menu resta aperto finché non si clicca fuori o ESC (niente chiusura al mouseleave)

    // Submenu items
    document.querySelectorAll('.custom-mega-menu-item.has-submenu').forEach((item, index) => {
        item.addEventListener('click', (e) => {
            e.preventDefault();
            handleSubmenuClick(item);
        });

        // Initial animation for items
        setTimeout(() => {
            addAnimationClass(item, 'animate-scale-in', index * CONFIG.animation.delay);
        }, 300);
    });

    // Chiusura a click fuori dal menu; blocca l'azione del link solo se il menu era aperto
    function handleClickOutside(e) {
        if (!elements.menuContainer) return;
        if (elements.menuContainer.contains(e.target)) return;
        const menuIsOpen = document.querySelector('.main-mega-menu-panel.active');
        if (!menuIsOpen) return;
        e.preventDefault();
        e.stopPropagation();
        handleMenuClose();
    }
    document.addEventListener('click', handleClickOutside, true);

    // ESC key to close menu
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            handleMenuClose();
        }
    });

    // Scroll to close menu
    let scrollTimeout;
    const handleScroll = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => handleMenuClose(), 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // =============================================================================
    // INITIALIZATION
    // =============================================================================

    // Initialize main slider
    initMainSlider();

    // Format category names to sentence case
    formatCategoryNames();

    // Also format names when panels become active (for dynamically loaded content)
    const observer = new MutationObserver(() => {
        formatCategoryNames();
    });

    // Observe changes to the menu container
    if (elements.menuContainer) {
        observer.observe(elements.menuContainer, {
            childList: true,
            subtree: true
        });
    }
}); 